<template>
  <VDialogV3
    id="package-card"
    position="right"
    width="600px"
    height="100vh"
    :entity="localPackage"
    :context-menu="editMode ? PackagesService.contextMenu : null"
    :on-context-menu-action="onContextMenuAction"
    :submit-disabled="saveDisabled"
    @submit="onSubmit"
  >
    <section>
      <VInput
        ref="nameInput"
        v-model="localPackage.name"
        label="Название"
        layout="vertical"
        required
        :validate-method="validateName"
      />

      <VInput
        ref="codeInput"
        v-model="localPackage.code"
        label="Код"
        layout="vertical"
        required
        :validate-method="validateCode"
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">

import Package            from '@/domains/Packages/Package';
import PackagesService    from '@/domains/Packages/PackagesService';

export default defineComponent({
  name: 'PackageCard',

  props: {
    package: {
      type:    Object,
      default: null,
    },
    id: {
      type:    Number,
      default: null,
    },
  },

  data() {
    return {
      PackagesService,

      localPackage: null as Package | null,
    };
  },

  computed: {
    editMode(): boolean {
      return this.localPackage?.isExisting ?? false;
    },

    /**
     * Причина запрета сохранения (строка → :submit-disabled показывает тултип).
     * Удалённый вид упаковки редактировать нельзя. Валидность полей ведёт сам VDialogV3.
     */
    saveDisabled(): string | null {
      if (this.localPackage?.isDeleted) {
        return 'Вид упаковки удалён';
      }
      else {
        return null;
      }
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localPackage = await PackagesService.get(this.id);
      }
      else if (this.package instanceof Package) {
        this.localPackage = this.$core.utils.cloneDeep(this.package);
      }
      else {
        this.localPackage = new Package();
      }
    },

    async onSubmit(): Promise<void> {
      let success: boolean;

      if (this.editMode) {
        success = await PackagesService.update(this.localPackage!);
      }
      else {
        success = await PackagesService.add(this.localPackage!);
      }

      if (success) {
        this.$core.dialogs.PackageCard.resolve();
      }
    },

    /**
     * Колбэк после действия пункта контекстного меню (refreshAfter !== false).
     * В карточке это «Удалить» — после удаления резолвим диалог: карточка
     * закроется, а showCard вернёт true, и реестр обновится (позиция исчезнет).
     * Перечитывания черновика здесь нет — карточка закрывается, затирать нечего.
     */
    onContextMenuAction(): void {
      this.$core.dialogs.PackageCard.resolve();
    },

    // ==================== Валидация ====================

    validateName(value: string): string | null | Promise<string | null> {
      if (typeof value !== 'string' || value.length === 0) { return null; }
      if (value.length > 200) { return 'Максимальная длина поля 200 символов'; }
      if (value === this.package?.name) { return null; }

      return PackagesService.getList({ name: value }).then(list => {
        return list.length > 0 ? 'Ошибка уникальности. Данное название уже существует' : null;
      });
    },

    validateCode(value: string): string | null | Promise<string | null> {
      if (typeof value !== 'string' || value.length === 0) { return null; }
      if (value.length > 10) { return 'Максимальная длина поля 10 символов'; }
      if (value === this.package?.code) { return null; }

      return PackagesService.getList({ code: value }).then(list => {
        return list.length > 0 ? 'Ошибка уникальности. Данный код уже существует' : null;
      });
    },
  },
});
</script>
